import { setLearnerArchive } from "@/api/services/main/learner";
import { IconEditor } from "@/assets/icons";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { useDialog } from "@/hooks/useDialog";
import { useVisible } from "@/hooks/useVisible";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { LEARNER_WIDTH_FULL } from "@/features/learner/constants/width";
import { LearnerAccountInfo } from "@/features/learner/components/LearnerAccountInfo";
import { LearnerBasicInfoDetail } from "@/features/learner/components/learner-detail/LearnerBasicInfoDetail";
import { LearnerExamInfoDetail } from "@/features/learner/components/learner-detail/LearnerExamInfoDetail";
import { LearnerOtherInfoDetail } from "@/features/learner/components/learner-detail/LearnerOtherInfoDetail";
import { LearnerResidenceInfoDetail } from "@/features/learner/components/learner-detail/LearnerResidenceInfoDetail";
import { LearnerStatusDetail } from "@/features/learner/components/learner-detail/LearnerStatusDetail";
import { LearnerCurriculumDetail } from "@/features/learner/components/learner-detail/LearnerCurriculumDetail";
import { CreateLearnerPlanModal } from "@/features/learner/components/CreateLearnerPlanModal";
import { LearnerTitle } from "@/features/learner/components/LearnerTitle";
import { AppRoutes } from "@/routes/config";
import { FCC } from "@/types/common";
import { getDynamicRoute } from "@/utils/url";
import { toast } from "@/utils/toast";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TLearnerForm } from "@/features/learner/types/learnerForm";
import { getIndustryPartTypes, useLearnerIndustryOptions, TLearnerIndustryWithCurriculum } from "@/features/learner/hook/useLearnerIndustryOptions";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { USER_TYPE } from "@/types/enum";
import { TIndustryExam } from "@/api/services/main/company/dtos/get-company-list.dto";
import { showError } from "@/utils/error";

type TLearnerDetailPageProps = {
  data?: Partial<TLearnerForm>;
};

export const LearnerDetailPage: FCC<TLearnerDetailPageProps> = ({ data }) => {
  const { userType } = useInitSetup();
  const isSupportUser = userType === USER_TYPE.SUPPORT_USER;
  const navigate = useNavigate();
  const { learnerId } = useParams();
  const queryClient = useQueryClient();
  const confirmModal = useDialog();
  const newPlanModal = useVisible();
  const [isArchiving, setIsArchiving] = useState(false);

  // Fallback to learner's saved industry when it has been hidden/removed from the list.
  const fallbackIndustry = useMemo<(Partial<TLearnerIndustryWithCurriculum> & { id: number; name: string }) | null>(() => {
    const ind = data?.industry;
    if (!ind?.id) return null;
    const savedExam = data?.learnerPlan?.exam;
    const fallbackExams: TIndustryExam[] = savedExam
      ? [
          {
            id: String(savedExam.id),
            periodName: savedExam.periodName,
            status: "",
            examMethod: "",
            examMonth: null,
          },
        ]
      : [];
    return {
      id: ind.id,
      name: ind.name,
      subjects: ind.subjects ?? [],
      exams: fallbackExams,
    };
  }, [data]);

  const { getIndustryById } = useLearnerIndustryOptions(fallbackIndustry);
  const selectedIndustry = getIndustryById(data?.industryId);

  const industryPartTypes = useMemo(() => getIndustryPartTypes(selectedIndustry), [selectedIndustry]);

  // Subjects (incl. 単語数/問題数) come straight from /industries — no merge with learner detail needed.
  const industrySubjects = useMemo(() => selectedIndustry?.subjects ?? [], [selectedIndustry]);

  const subjectCount = useMemo(() => {
    const subjects = selectedIndustry?.subjects ?? [];
    if (subjects.length) {
      if (!industryPartTypes.length) return subjects.length;
      const parts = data?.learnerPlan?.partTypes ?? [];
      return subjects.filter((s) => parts.includes(s.partType)).length;
    }
    // Fallback when /industries doesn't provide the subject list → use the flat subjectCount.
    return selectedIndustry?.subjectCount ?? null;
  }, [selectedIndustry, industryPartTypes, data?.learnerPlan?.partTypes]);

  const toEditRoute = () => {
    const learnerEditRoute = getDynamicRoute(AppRoutes.learnerEdit, { learnerId });
    navigate(learnerEditRoute);
  };

  const handleSetArchive = async (isArchive: boolean) => {
    if (!learnerId) return;

    try {
      setIsArchiving(true);
      await setLearnerArchive(learnerId, isArchive);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNER.FETCH_LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNER.FETCH_DETAIL, learnerId] });
      toast.success(API_SUCCESS_MESSAGE);
      navigate(AppRoutes.learnerList);
    } catch (e) {
      showError(e);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleViewPlanList = useCallback(() => {
    navigate(getDynamicRoute(AppRoutes.learnerPlanList, { learnerId }));
  }, [navigate, learnerId]);

  return (
    <>
      <LearnerTitle
        fullName={data?.fullName ?? undefined}
        planStatus={data?.learnerPlan?.planStatus ?? undefined}
        createdAt={data?.createdAt}
        updatedAt={data?.updatedAt}
      />
      <Stack mt={1} width={`${LEARNER_WIDTH_FULL}px`} spacing={2}>
        <MenuNavigate
          menuPrefix={
            <Typography variant="body14Bold" sx={{ mb: 1 }}>
              {data?.fullName}
            </Typography>
          }
          contents={[
            { title: "ステータス", content: <LearnerStatusDetail data={data} /> },
            { title: "基本情報", content: <LearnerBasicInfoDetail data={data} /> },
            { title: "在留資格情報", content: <LearnerResidenceInfoDetail data={data} /> },
            { title: "分野・日本語能力・試験記録", content: <LearnerExamInfoDetail data={data} /> },
            { title: "その他の情報", content: <LearnerOtherInfoDetail data={data} /> },
            {
              title: "受講カリキュラム",
              content: <LearnerCurriculumDetail
                data={data}
                subjectCount={subjectCount}
                industryPartTypes={industryPartTypes}
                subjects={industrySubjects}
                mockExamQuestionCount={selectedIndustry?.mockExamQuestionCount ?? null}
                onOpenNewPlan={newPlanModal.open}
                onViewPlanList={handleViewPlanList}
              />,
            },
            // Hide the account-management section when the learning app is turned OFF.
            ...(data?.isUsingLearningApp !== false
              ? [
                  {
                    title: "学習アプリのアカウント管理",
                    content: (
                      <LearnerAccountInfo
                        data={data}
                        disabledAccountBtn={!!isSupportUser}
                        industryHasNoSubjects={!!data?.industryId && !!selectedIndustry && !selectedIndustry.subjects?.length}
                        isDetailPage
                      />
                    ),
                  },
                ]
              : []),
          ]}
          contentWidth="900px"
          contentAffix={
            !isSupportUser ? (
              <FooterContainer>
                <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" gap={2}>
                    <Button variant="ghost" size="sm" onClick={() => navigate(AppRoutes.learnerList)}>
                      戻る
                    </Button>
                    {data?.isArchive ? (
                      <Button variant="ghost" size="sm" onClick={() => handleSetArchive(false)} disabled={isArchiving}>
                        アーカイブから戻す
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => confirmModal.show()} disabled={isArchiving}>
                        アーカイブ
                      </Button>
                    )}
                  </Stack>
                  <Stack direction="row" gap={2}>
                    <Button variant="outline" size="sm" startIcon={<IconEditor />} onClick={toEditRoute}>
                      編集
                    </Button>
                  </Stack>
                </Stack>
              </FooterContainer>
            ) : (
              <FooterContainer>
                <Stack gap={1} direction="row" justifyContent="space-start" alignItems="center">
                  <Button variant="ghost" size="sm" onClick={() => navigate(AppRoutes.learnerList)}>
                    戻る
                  </Button>
                </Stack>
              </FooterContainer>
            )
          }
        />
      </Stack>
      <ConfirmSaveModal open={confirmModal.open} onOk={() => handleSetArchive(true)} onClose={confirmModal.cancel}>
        <Typography>学習者をアーカイブしてもよろしいですか？</Typography>
      </ConfirmSaveModal>
      {learnerId && (
        <CreateLearnerPlanModal
          modal={newPlanModal}
          learnerId={learnerId}
          industryPartTypes={industryPartTypes}
          industryExams={selectedIndustry?.exams ?? []}
          defaultValues={data?.learnerPlan}
          subjects={industrySubjects}
          mockExamQuestionCount={selectedIndustry?.mockExamQuestionCount ?? null}
        />
      )}
    </>
  );
};
